import { useEffect, useMemo, useState, type ChangeEvent } from "react";
import { createPortal } from "react-dom";
import { Download, ImagePlus, Palette, RotateCcw, X } from "lucide-react";
import { freshSession } from "@/lib/api";
import { Button, Modal, useToast } from "@/ui";
import {
  COUNTER_POSTER_PRESETS,
  createCounterPosterHtml,
  type CounterPosterDesign,
  type CounterPosterLanguage,
  type CounterPosterPreset,
} from "@/lib/counterPosterDesign";

const MAX_BACKGROUND_BYTES = 5 * 1024 * 1024;

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read this image."));
    reader.onload = () => typeof reader.result === "string" ? resolve(reader.result) : reject(new Error("Could not read this image."));
    reader.readAsDataURL(blob);
  });
}

export function CounterPosterDesigner({
  open,
  onClose,
  qrPngUrl,
  orgName,
  appName,
  orgLogoUrl,
  counterName,
  counterPhone,
  brandColor,
  brandAccent,
}: {
  open: boolean;
  onClose: () => void;
  qrPngUrl: string;
  orgName: string;
  appName: string;
  orgLogoUrl: string | null;
  counterName: string;
  counterPhone: string | null;
  brandColor: string;
  brandAccent: string;
}) {
  const toast = useToast();
  const defaultPreset = COUNTER_POSTER_PRESETS[0]!;
  const [language, setLanguage] = useState<CounterPosterLanguage>("hi");
  const [preset, setPreset] = useState<CounterPosterPreset>("brand");
  const [primary, setPrimary] = useState(brandColor);
  const [accent, setAccent] = useState(brandAccent);
  const [customColors, setCustomColors] = useState(false);
  const [backgroundDataUrl, setBackgroundDataUrl] = useState<string | null>(null);
  const [backgroundName, setBackgroundName] = useState("");
  const [backgroundOverlay, setBackgroundOverlay] = useState(42);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState<string | null>(null);

  useEffect(() => {
    if (preset === "brand" && !customColors) setPrimary(brandColor);
  }, [brandColor, preset, customColors]);
  useEffect(() => {
    if (preset === "brand" && !customColors) setAccent(brandAccent);
  }, [brandAccent, preset, customColors]);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setQrLoading(true);
    setQrError(null);
    setQrDataUrl("");
    void (async () => {
      try {
        await freshSession();
        const response = await fetch(qrPngUrl, { credentials: "include" });
        if (!response.ok) throw new Error("Could not load the registration QR code.");
        const dataUrl = await blobToDataUrl(await response.blob());
        if (!cancelled) setQrDataUrl(dataUrl);
      } catch (error) {
        if (!cancelled) setQrError(error instanceof Error ? error.message : "Could not load the registration QR code.");
      } finally {
        if (!cancelled) setQrLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, qrPngUrl]);

  const design: CounterPosterDesign = useMemo(() => ({
    orgName,
    appName,
    orgLogoUrl,
    counterName,
    counterPhone,
    qrDataUrl,
    language,
    preset,
    primary,
    accent,
    backgroundDataUrl,
    backgroundOverlay,
  }), [orgName, appName, orgLogoUrl, counterName, counterPhone, qrDataUrl, language, preset, primary, accent, backgroundDataUrl, backgroundOverlay]);
  const previewHtml = useMemo(
    () => qrDataUrl ? createCounterPosterHtml(design) : "",
    [design, qrDataUrl],
  );

  function selectPreset(nextPreset: CounterPosterPreset): void {
    const selected = COUNTER_POSTER_PRESETS.find((item) => item.id === nextPreset) ?? defaultPreset;
    setPreset(nextPreset);
    setPrimary(nextPreset === "brand" ? brandColor : selected.primary);
    setAccent(nextPreset === "brand" ? brandAccent : selected.accent);
    setCustomColors(false);
  }

  async function chooseBackground(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      toast.error("Choose a PNG, JPG, or WebP image.");
      return;
    }
    if (file.size > MAX_BACKGROUND_BYTES) {
      toast.error("Background images must be 5 MB or smaller.");
      return;
    }
    try {
      setBackgroundDataUrl(await blobToDataUrl(file));
      setBackgroundName(file.name);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not load this image.");
    }
  }

  function downloadPoster(): void {
    if (!qrDataUrl) return;
    const printWindow = window.open("about:blank", "_blank");
    if (!printWindow) {
      toast.error("Allow pop-ups for this site to print or save the poster.");
      return;
    }
    printWindow.document.open();
    printWindow.document.write(createCounterPosterHtml(design, true));
    printWindow.document.close();
    printWindow.opener = null;
  }

  function resetDesign(): void {
    selectPreset("brand");
    setLanguage("hi");
    setBackgroundDataUrl(null);
    setBackgroundName("");
    setBackgroundOverlay(42);
  }

  return open ? createPortal((
    <Modal
      description="Choose a palette or preset, add an optional background, and preview the A4 poster before saving it."
      footer={
        <>
          <Button icon={<RotateCcw aria-hidden="true" className="size-4" />} onClick={resetDesign} variant="secondary">Reset design</Button>
          <Button disabled={!qrDataUrl || qrLoading} icon={<Download aria-hidden="true" className="size-4" />} loading={qrLoading} onClick={downloadPoster}>
            Download / print poster
          </Button>
        </>
      }
      onClose={onClose}
      open={open}
      title="Design your registration poster"
      width="2xl"
    >
      <div className="grid gap-5 lg:grid-cols-[minmax(0,0.9fr)_minmax(300px,1fr)]">
        <div className="space-y-5">
          <section aria-label="Poster language">
            <h3 className="text-sm font-semibold text-ink">Language</h3>
            <div className="mt-2 inline-flex rounded-lg border border-rule bg-surface p-1" role="group" aria-label="Choose poster language">
              {(["hi", "en"] as const).map((value) => (
                <button
                  aria-pressed={language === value}
                  className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${language === value ? "bg-brand text-on-brand shadow-control" : "text-muted hover:text-ink"}`}
                  key={value}
                  onClick={() => setLanguage(value)}
                  type="button"
                >
                  {value === "hi" ? "हिंदी" : "English"}
                </button>
              ))}
            </div>
          </section>

          <section aria-label="Poster presets">
            <div className="flex items-center gap-2">
              <Palette aria-hidden="true" className="size-4 text-brand-ink" />
              <h3 className="text-sm font-semibold text-ink">Color palette & preset</h3>
            </div>
            <p className="mt-1 text-xs text-muted">Start with your organization colors or choose a ready-made look.</p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {COUNTER_POSTER_PRESETS.map((item) => {
                const selected = preset === item.id && !customColors;
                const swatchPrimary = item.id === "brand" ? brandColor : item.primary;
                return (
                  <button
                    aria-pressed={selected}
                    className={`rounded-lg border p-2.5 text-left transition-colors ${selected ? "border-brand ring-2 ring-brand/20" : "border-rule hover:border-brand-border"}`}
                    key={item.id}
                    onClick={() => selectPreset(item.id)}
                    type="button"
                  >
                    <span className="flex h-9 items-center gap-1.5 overflow-hidden rounded-md border border-black/5 p-1" style={{ background: item.backgroundImage }}>
                      <i className="size-5 rounded-full border border-white/60 shadow-sm" style={{ backgroundColor: swatchPrimary }} />
                      <i className="size-5 rounded-full border border-white/60 shadow-sm" style={{ backgroundColor: item.accent }} />
                      <i className="ml-auto size-5 rounded-full border border-white/60 shadow-sm" style={{ backgroundColor: item.paper }} />
                    </span>
                    <span className="mt-2 block text-xs font-semibold text-ink">{item.name}</span>
                    <span className="mt-0.5 block text-[11px] leading-snug text-muted">{item.description}</span>
                  </button>
                );
              })}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 rounded-lg border border-rule bg-surface p-3">
              <ColorControl label="Main color" value={primary} onChange={(value) => { setPrimary(value); setCustomColors(true); }} />
              <ColorControl label="Accent color" value={accent} onChange={(value) => { setAccent(value); setCustomColors(true); }} />
              {customColors ? <p className="col-span-2 text-xs text-muted">Custom palette selected</p> : null}
            </div>
          </section>

          <section aria-label="Poster background">
            <div className="flex items-center gap-2">
              <ImagePlus aria-hidden="true" className="size-4 text-brand-ink" />
              <h3 className="text-sm font-semibold text-ink">Background image <span className="font-normal text-muted">(optional)</span></h3>
            </div>
            <p className="mt-1 text-xs text-muted">Use a PNG, JPG, or WebP up to 5 MB. It is added to this poster only.</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <label className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-md border border-rule bg-raised px-3 text-sm font-semibold text-ink shadow-control transition-colors hover:border-brand-border hover:text-brand-ink">
                <ImagePlus aria-hidden="true" className="size-4" />
                Upload background
                <input accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={(event) => void chooseBackground(event)} type="file" />
              </label>
              {backgroundDataUrl ? (
                <>
                  <span className="max-w-44 truncate text-xs text-muted" title={backgroundName}>{backgroundName}</span>
                  <button aria-label="Remove background image" className="grid size-9 place-items-center rounded-md text-muted hover:bg-ink/5 hover:text-ink" onClick={() => { setBackgroundDataUrl(null); setBackgroundName(""); }} type="button">
                    <X aria-hidden="true" className="size-4" />
                  </button>
                </>
              ) : null}
            </div>
            {backgroundDataUrl ? (
              <label className="mt-3 block text-xs font-medium text-ink">
                Dark overlay for readable text <span className="ml-1 text-muted">{backgroundOverlay}%</span>
                <input className="mt-2 block w-full accent-brand" max="80" min="0" onChange={(event) => setBackgroundOverlay(Number(event.currentTarget.value))} type="range" value={backgroundOverlay} />
              </label>
            ) : null}
          </section>
        </div>

        <section aria-label="Live poster preview" className="min-w-0">
          <div className="mb-2 flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-ink">Live preview</h3>
              <p className="mt-0.5 text-xs text-muted">A4 portrait · QR stays on a white print-safe panel</p>
            </div>
            <span className="rounded-full bg-brand-light px-2.5 py-1 text-xs font-semibold text-brand-ink">{customColors ? "Custom" : COUNTER_POSTER_PRESETS.find((item) => item.id === preset)?.name}</span>
          </div>
          <div className="overflow-hidden rounded-xl border border-rule bg-[#e8e4df] p-2 shadow-control">
            {qrLoading ? (
              <div className="grid aspect-[210/297] place-items-center rounded-lg bg-white text-sm text-muted">Preparing poster preview…</div>
            ) : qrError ? (
              <div className="grid aspect-[210/297] place-items-center rounded-lg bg-white p-6 text-center text-sm text-danger" role="alert">{qrError}</div>
            ) : previewHtml ? (
              <iframe className="block aspect-[210/297] w-full rounded-lg border-0 bg-white" sandbox="allow-same-origin" srcDoc={previewHtml} title={`${counterName} registration poster preview`} />
            ) : (
              <div className="grid aspect-[210/297] place-items-center rounded-lg bg-white text-sm text-muted">Waiting for the QR code…</div>
            )}
          </div>
        </section>
      </div>
    </Modal>
  ), document.body) : null;
}

function ColorControl({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="flex min-w-0 items-center gap-2 text-xs font-medium text-ink">
      <input aria-label={label} className="size-9 shrink-0 cursor-pointer rounded-md border border-rule bg-transparent p-1" onChange={(event) => onChange(event.currentTarget.value)} type="color" value={value} />
      <span className="min-w-0">
        <span className="block">{label}</span>
        <span className="mt-0.5 block font-mono text-[11px] text-muted">{value.toUpperCase()}</span>
      </span>
    </label>
  );
}
